import PropTypes from 'prop-types'
import React, { useContext, useEffect, useState } from 'react'
import { StoreState } from '../../Store'
import { colors } from '../../styles/vars/colors.style'
import { countryCookieCheck, externalLinkCheck } from '../../utils/utils'
import { Cta } from '../TextStyles'
import {
  ButtonContent,
  ButtonIcon,
  ButtonMain,
  ButtonMask,
  ButtonMaskChild,
} from './index.style'

/**
 *
 * @prop variant
 * Defines the visual style
 * @prop href
 * If defined this renders the component as an anchor element with an href attribute <a href={href} />
 * @prop inline
 * If true this renders the component as a div. This is necessary when wrapping in a PageTransitionLink component
 * @prop label
 * If defined this adds an aria-label to the component. Necessary for icon only buttons.
 */

const Button = ({
  variant = 'default',
  color = colors.dark,
  mask = true,
  disabled,
  href,
  inline,
  label,
  target,
  iconLeft,
  iconRight,
  onClick,
  children,
  ariaHidden,
  ...props
}) => {
  const store = useContext(StoreState)
  const [url, setUrl] = useState(href ? href : null)

  useEffect(() => {
    setUrl(prevUrl => {
      return (
        externalLinkCheck(prevUrl, countryCookieCheck(store.countryCookie)) ||
        null
      )
    })
  }, [store.countryCookie])

  return (
    <ButtonMain
      as={url ? 'a' : inline ? 'div' : 'button'}
      href={url}
      variant={variant}
      color={color}
      disabled={disabled}
      aria-label={label}
      onClick={onClick}
      target={target || url ? '_blank' : undefined}
      aria-hidden={ariaHidden}
      {...props}
    >
      <ButtonContent>
        {iconLeft && <ButtonIcon left>{iconLeft}</ButtonIcon>}
        {children && !mask && <Cta>{children}</Cta>}
        {children && mask && (
          <ButtonMask>
            <ButtonMaskChild>
              <Cta aria-hidden={ariaHidden}>{children}</Cta>
            </ButtonMaskChild>
            <ButtonMaskChild aria-hidden="true">
              <Cta aria-hidden="true">{children}</Cta>
            </ButtonMaskChild>
          </ButtonMask>
        )}
        {iconRight && <ButtonIcon right>{iconRight}</ButtonIcon>}
      </ButtonContent>
    </ButtonMain>
  )
}

Button.propTypes = {
  variant: PropTypes.oneOf(['outline', 'fill', 'underline', 'default']),
  disabled: PropTypes.bool,
  inline: PropTypes.bool,
  mask: PropTypes.bool,
  color: PropTypes.string,
  target: PropTypes.string,
  href: PropTypes.string,
  label: PropTypes.string,
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
  onClick: PropTypes.func,
  children: PropTypes.node,
}

export default Button
